import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiX,
  HiEye,
  HiTrash,
  HiPaperClip,
  HiChevronLeft,
  HiChevronRight,
  HiDownload,
} from 'react-icons/hi';
import { MdDelete } from 'react-icons/md';
import config from '../../config';
import { fetchFeedbackList, deleteFeedback } from '../../services/feedbackService';
import { useSearchParams } from 'react-router-dom';

const feedbackCache = {
  feedbackData: null,
  page: 1,
  totalRecords: 0,
  firstLoad: true,
};

const resolveAttachmentValue = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return resolveAttachmentValue(value[0]);
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return (
      value.url ||
      value.path ||
      value.link ||
      value.href ||
      value.Location ||
      value.location ||
      null
    );
  }
  return null;
};

const resolveAttachmentName = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) return resolveAttachmentName(value[0]);
  if (typeof value === 'string') {
    const parts = value.split('/');
    return parts[parts.length - 1];
  }
  if (typeof value === 'object') {
    return (
      value.name ||
      value.filename ||
      value.fileName ||
      value.originalName ||
      value.originalname ||
      resolveAttachmentName(resolveAttachmentValue(value)) ||
      ''
    );
  }
  return '';
};

const buildAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = config.baseApi || '';
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
};

const isImageUrl = (url = '') => {
  const cleanUrl = url.split('?')[0].toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(cleanUrl);
};

const extractAttachmentInfo = (item) => {
  const rawAttachment =
    item.attachment ||
    item.attachmentUrl ||
    item.attachment_url ||
    item.file ||
    item.fileUrl ||
    item.file_url ||
    item.document ||
    item.screenshot ||
    item.media ||
    item.mediaUrl ||
    (Array.isArray(item.attachments) ? item.attachments[0] : null);

  const rawUrl = resolveAttachmentValue(rawAttachment);
  const url = buildAbsoluteUrl(rawUrl);
  const name =
    resolveAttachmentName(rawAttachment) ||
    (url ? resolveAttachmentName(url) : '');

  return { url, name };
};

const normalizeFeedbackItem = (item, index) => {
  const { url: attachment, name: attachmentName } = extractAttachmentInfo(item);
  const dateRaw = item.date || item.createdAt || item.created_at || item.created_on;
  const parsedDate = dateRaw ? new Date(dateRaw) : null;
  const date =
    parsedDate && !isNaN(parsedDate.valueOf())
      ? parsedDate.toLocaleString()
      : dateRaw;

  return {
    ...item,
    id:
      item.id ||
      item._id ||
      item.feedbackId ||
      item.uuid ||
      item.key ||
      `feedback-${index}`,
    email: item.email || item.userEmail || item.user || item.contactEmail || '',
    subject: item.subject || item.title || 'No subject',
    description: item.description || item.message || item.feedback || '',
    status: item.status || item.state || 'Pending',
    date,
    attachment,
    attachmentName,
  };
};

const Feedback = () => {
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(feedbackCache.page || 1);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [feedbackData, setFeedbackData] = useState(feedbackCache.feedbackData || []);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(feedbackCache.totalRecords || 0);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const itemsPerPage = 10;

  // Get date filters from URL
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  // Reset to first page when date filters change
  useEffect(() => {
    if (startDate && endDate && currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  
  // Guard flags to prevent duplicate API calls
  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false);
  const searchDebounceTimerRef = useRef(null);
  const lastCallParamsRef = useRef({ page: null, search: null, startDate: null, endDate: null });

  const getPaginationRange = () => {
    if (totalPages === 0) return [];

    const totalPageNumbers = 5; // count of page numbers to show between first/last
    const totalBlocks = totalPageNumbers + 2; // incl. first and last

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Debounce search query - only run when searchQuery changes, not on initial mount
  useEffect(() => {
    // Skip debouncing on initial mount
    if (isInitialMountRef.current) {
      return;
    }

    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to first page when search changes
      setCurrentPage((currentPage) => currentPage !== 1 ? 1 : currentPage);
    }, 500); // 500ms debounce

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]); // Only react to searchQuery changes

  // Fetch feedback data from API
  const loadFeedbackData = async () => {
    const currentParams = { page: currentPage, search: debouncedSearch, startDate, endDate };
    const lastParams = lastCallParamsRef.current;
    
    const isFirstLoad = feedbackCache.firstLoad;
    const isSearchChange = debouncedSearch !== (feedbackCache.lastSearch || '');
    const isDateChange = startDate !== (feedbackCache.lastStartDate || '') || endDate !== (feedbackCache.lastEndDate || '');
    
    // Prevent duplicate calls with same parameters
    const isSameParams = lastParams.page === currentParams.page && 
                        lastParams.search === currentParams.search &&
                        lastParams.startDate === currentParams.startDate &&
                        lastParams.endDate === currentParams.endDate;
    
    // Block if same parameters (except on very first mount)
    if (isSameParams) {
      if (isInitialMountRef.current) {
        if (lastParams.page !== null) {
          return; // Already called with these params, skip
        }
      } else {
        return; // After initial mount, always block same params
      }
    }

    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }

    if (isFirstLoad || isSearchChange || isDateChange) {
      setIsLoading(true);
    }

    // Mark parameters as used immediately
    lastCallParamsRef.current = currentParams;
    isLoadingRef.current = true;
    setError(null);
    try {
      const filters = {};
      const encodedSearch = encodeURIComponent(debouncedSearch || '');
      const response = await fetchFeedbackList(currentPage, itemsPerPage, encodedSearch, filters, startDate, endDate);

      // Set loading to false immediately after API resolves
      setIsLoading(false);

      // Normalize API response structure (some backends return success:false even on success)
      const extractItems = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res?.data?.docs)) return res.data.docs;
        if (Array.isArray(res?.data?.list)) return res.data.list;
        if (Array.isArray(res?.result)) return res.result;
        if (Array.isArray(res?.list)) return res.list;
        return [];
      };

      const items = extractItems(response);
      const normalizedItems = items.map((item, idx) =>
        normalizeFeedbackItem(item, idx)
      );
      
      // Extract total from various possible response structures
      const total =
        response?.total ??
        response?.data?.total ??
        response?.data?.totalDocs ??
        response?.data?.count ??
        response?.data?.pagination?.total ??
        response?.pagination?.total ??
        response?.data?.pagination?.totalRecords ??
        response?.data?.pagination?.totalCount ??
        response?.data?.data?.total ??
        response?.data?.data?.pagination?.total ??
        response?.result?.total ??
        response?.result?.pagination?.total ??
        null;
      
      // Calculate total: use API total if available, otherwise estimate based on items
      let computedTotal = total;
      if (computedTotal === null || computedTotal === undefined) {
        // Total not found in API response - estimate based on items
        if (normalizedItems.length === itemsPerPage) {
          // Full page suggests more pages might exist - estimate conservatively
          computedTotal = currentPage * itemsPerPage + 1;
        } else if (normalizedItems.length > 0) {
          // Less than full page means we're on the last page
          computedTotal = (currentPage - 1) * itemsPerPage + normalizedItems.length;
        } else {
          computedTotal = 0;
        }
      }
      // If total is explicitly 0 from API, keep it as 0 (no records)

      // If the server does not paginate (returns all items), slice on the client.
      // If the server paginates (returns <= itemsPerPage and a larger total), keep as is.
      const serverPaginated =
        normalizedItems.length <= itemsPerPage ||
        computedTotal > normalizedItems.length ||
        Boolean(response?.data?.pagination);

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      const paginatedItems = serverPaginated
        ? normalizedItems
        : normalizedItems.slice(start, end);

      const successFlag =
        response?.success === true ||
        response?.status === true ||
        response?.code === 200 ||
        /success/i.test(response?.message || '') ||
        items.length > 0;

      if (successFlag) {
        setFeedbackData(paginatedItems);
        setTotalRecords(computedTotal);
        if (paginatedItems.length === 0) {
          setError(null); // Clear error, let the empty state message show
        } else {
          setError(null);
        }
        
        feedbackCache.feedbackData = paginatedItems;
        feedbackCache.page = currentPage;
        feedbackCache.totalRecords = computedTotal;
        feedbackCache.lastSearch = debouncedSearch;
        feedbackCache.lastStartDate = startDate;
        feedbackCache.lastEndDate = endDate;
        feedbackCache.firstLoad = false;
      } else {
        const errorMsg = response?.message || 'API returned an error';
        setError(errorMsg);
        feedbackCache.firstLoad = false;
      }
    } catch (err) {
      const errorMsg = err.message || 'Error loading feedback data. Please try again.';
      setError(errorMsg);
      setIsLoading(false);
      feedbackCache.firstLoad = false;
    } finally {
      // Guarantee loader stops even if there's an unexpected error
      setIsLoading(false);
      isLoadingRef.current = false;
      // Mark initial mount as complete after first API call
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
    }
  };

  // Load feedback data on mount and when page/debounced search/date filters change - single useEffect for API call
  useEffect(() => {
    const isSearchChange = debouncedSearch !== (feedbackCache.lastSearch || '');
    const isDateChange = startDate !== (feedbackCache.lastStartDate || '') || endDate !== (feedbackCache.lastEndDate || '');
    const hasCache = feedbackCache.feedbackData !== null && !feedbackCache.firstLoad && !isSearchChange && !isDateChange;
    
    if (hasCache && isInitialMountRef.current && currentPage === feedbackCache.page && debouncedSearch === (feedbackCache.lastSearch || '') && startDate === (feedbackCache.lastStartDate || '') && endDate === (feedbackCache.lastEndDate || '')) {
      setFeedbackData(feedbackCache.feedbackData);
      setCurrentPage(feedbackCache.page);
      setTotalRecords(feedbackCache.totalRecords);
      lastCallParamsRef.current = { page: currentPage, search: debouncedSearch, startDate, endDate };
      isInitialMountRef.current = false;
      return;
    }
    
    loadFeedbackData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, startDate, endDate]);

  // Calculate pagination values
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRecords);

  const handleView = (item) => {
    setSelectedFeedback(item);
    setShowModal(true);
  };

  const openAttachmentPreview = (url, name) => {
    if (!url) return;
    setAttachmentPreview({
      url,
      name: name || 'Attachment',
    });
  };

  const closeAttachmentPreview = () => setAttachmentPreview(null);

  const downloadAttachment = (url, name) => {
    if (!url) return;
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'attachment';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      // console.error('Error downloading attachment:', err);
      // Fallback: open in new tab if download fails
      window.open(url, '_blank');
    }
  };

  const handleDelete = (item) => {
    setFeedbackToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!feedbackToDelete) return;

    setDeleting(true);
    try {
      const feedbackId = feedbackToDelete.id || feedbackToDelete._id || feedbackToDelete.feedbackId;
      if (!feedbackId) {
        throw new Error('Feedback ID not found');
      }

      await deleteFeedback(feedbackId);

      // Reload data after successful deletion
      await loadFeedbackData();

      setShowDeleteModal(false);
      setFeedbackToDelete(null);
    } catch (err) {
      const errorMsg = err.message || 'Error deleting feedback. Please try again.';
      setError(errorMsg);
      // Keep modal open on error so user can retry
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setFeedbackToDelete(null);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const truncateText = (text = '', maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">Feedback Management</h1>
            </div>
            {/* <p className="text-sm md:text-base text-gray-600">Manage and review user feedback submissions</p> */}
          </div>

          {/* Filters Bar */}
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:justify-between gap-3 md:gap-4">
              {/* Search Input */}
              <div className=" w-48 md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none focus:border-[#ECECEC] bg-white"
                  />
                </div>
              </div>

            </div>
          </div>

          {/* Loader or Table View */}
          {isLoading ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : feedbackData.length === 0 && !error ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500 pointer-events-auto">
              No feedback has been submitted yet.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full table-auto border-collapse break-words">
                <thead className="bg-white border-b border-[#ECECEC]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-10 md:px-4 py-3 text-left text-sm font-semibold whitespace-nowrap text-gray-500 uppercase tracking-wider">
                      Attachment
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#ECECEC]">
                  {error ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-6 text-center text-red-500">
                        {error}
                      </td>
                    </tr>
                  ) : (
                    feedbackData.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-white/30 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-2.5 text-sm whitespace-nowrap text-gray-900 break-words">
                          {item.email}
                        </td>
                        <td className="px-2 py-2.5 text-sm  whitespace-nowrap text-gray-900 break-words">
                          {item.subject}
                        </td>
                        <td className=" px-5 md:px-3 py-2.5 text-sm line-clamp-2  text-gray-900 ">
                          {truncateText(item.description)}
                        </td>
                        <td className="px-9 md:px-3 py-2.5  text-sm whitespace-nowrap break-words">
                          {item.attachment ? (
                            <span className="inline-flex items-center gap-1 text-sm text-gray-900">
                              <HiPaperClip className="w-4 h-4" />
                              {item.attachmentName || 'Attachment'}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {item.attachment ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadAttachment(item.attachment, item.attachmentName);
                                }}
                                className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                style={{ backgroundColor: '#F2E2F7' }}
                                title="Download"
                              >
                                <HiDownload className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleView(item);
                                }}
                                className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                style={{ backgroundColor: '#F2E2F7' }}
                                title="View"
                              >
                                <HiEye className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item);
                              }}
                              className="bg-[#FDDDDD]  text-[#F76464] p-1.5 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <MdDelete className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === currentPage
                            ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                            : 'text-gray-900 hover:bg-gray-100'
                            }`}
                          aria-current={page === currentPage ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      {showModal && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-full flex flex-col no-scrollbar">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800">Feedback Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto flex-1 no-scrollbar">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-550">Email</label>
                <p className="mt-1 text-[#212121]">{selectedFeedback.email}</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-550">Date</label>
                <p className="mt-1 text-[#212121]">{selectedFeedback.date}</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-550">Subject</label>
                <p className="mt-1 text-[#212121]">{selectedFeedback.subject}</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-550">Attachment</label>
                <div className="mt-1">
                  {selectedFeedback.attachment ? (
                    <a
                      href={selectedFeedback.attachment}
                      onClick={(e) => {
                        e.preventDefault();
                        openAttachmentPreview(
                          selectedFeedback.attachment,
                          selectedFeedback.attachmentName
                        );
                      }}
                      className="flex items-start gap-2 text-xs md:text-sm text-[#212121]  font-medium break-words"
                    >
                      <HiPaperClip className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span className="break-words word-break break-all">{selectedFeedback.attachmentName || 'View Attachment'}</span>
                    </a>
                  ) : (
                    <p className="text-gray-400">No attachment provided.</p>
                  )}
                </div>
              </div>
              <div className="space-y-1 md:col-span-2 -mt-4 ">
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="mt-1 text-gray-900 whitespace-pre-wrap">{selectedFeedback.description}</p>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      {attachmentPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-full overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-2">
                <HiPaperClip className="w-5 h-5 text-purple-600" />
                <h3 className="text-xs md:text-base font-semibold text-gray-800">
                  {attachmentPreview.name || 'Attachment'}
                </h3>
              </div>
              <button
                onClick={closeAttachmentPreview}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 bg-gray-50 overflow-auto flex items-center justify-center p-4">
              {isImageUrl(attachmentPreview.url) ? (
                <img
                  src={attachmentPreview.url}
                  alt={attachmentPreview.name || 'Attachment'}
                  className="max-h-[65vh] max-w-full object-contain"
                />
              ) : (
                <iframe
                  src={attachmentPreview.url}
                  title={attachmentPreview.name || 'Attachment Preview'}
                  className="w-full max-w-5xl h-[65vh] border-0"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && feedbackToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete Feedback</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete this feedback from this user?
              </p>

            </div>
            <div className="px-6 py-4  flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;

